import { Button, HStack, Text } from "@chakra-ui/react";

interface PaginationProps {
  page: number;
  limit: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

const getPaginationRange = (
  totalPages: number,
  currentPage: number,
  siblingCount = 1
) => {
  const totalPageNumbers = siblingCount + 5;

  if (totalPageNumbers >= totalPages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

  const firstPageIndex = 1;
  const lastPageIndex = totalPages;

  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
    return [...leftRange, "...", totalPages];
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from(
      { length: rightItemCount },
      (_, i) => totalPages - rightItemCount + i + 1
    );
    return [firstPageIndex, "...", ...rightRange];
  }

  if (shouldShowLeftDots && shouldShowRightDots) {
    const middleRange = Array.from(
      { length: rightSiblingIndex - leftSiblingIndex + 1 },
      (_, i) => leftSiblingIndex + i
    );
    return [firstPageIndex, "...", ...middleRange, "...", lastPageIndex];
  }

  return [];
};

export function Pagination({
  page,
  limit,
  totalCount,
  onPageChange,
}: PaginationProps) {
  const totalPages = Math.ceil(totalCount / limit);
  const isFirstPage = page === 1;
  const isLastPage = page === totalPages;

  const paginationRange = getPaginationRange(totalPages, page);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <HStack mt={4} justify="space-between" w="full">
      <Text fontSize="sm" color="gray.600">
        Página {page} de {totalPages}
      </Text>
      <HStack>
        <Button
          onClick={() => onPageChange(page - 1)}
          isDisabled={isFirstPage}
          size="sm"
        >
          Anterior
        </Button>
        {paginationRange?.map((pageNumber, index) => {
          if (typeof pageNumber === "string") {
            return (
              <Text key={`dots-${index}`} px={2}>
                ...
              </Text>
            );
          }
          return (
            <Button
              key={pageNumber}
              onClick={() => onPageChange(pageNumber)}
              isActive={pageNumber === page}
              size="sm"
            >
              {pageNumber}
            </Button>
          );
        })}
        <Button
          onClick={() => onPageChange(page + 1)}
          isDisabled={isLastPage}
          size="sm"
        >
          Próxima
        </Button>
      </HStack>
    </HStack>
  );
}
